import { useCallback, useEffect, useRef, useState } from "react";
import { Device, type Call } from "@twilio/voice-sdk";
import { apiClient } from "@lib/apiClient";

export type VoiceStatus =
  | "idle"
  | "initializing"
  | "ready"
  | "connecting"
  | "in-call"
  | "incoming"
  | "error"
  | "offline";

interface IncomingInfo {
  call: Call;
  from: string;
}

export interface UseTwilioVoice {
  device: Device | null;
  activeCall: Call | null;
  /** Internal (backend) id of the active call — required for transfer/hangup APIs. */
  activeCallId: string | null;
  incoming: IncomingInfo | null;
  status: VoiceStatus;
  identity: string | null;
  error: string | null;
  dial: (to: string, opts?: { record?: boolean; conferenceRoomId?: string }) => Promise<void>;
  answer: () => void;
  hangup: () => void;
  mute: () => void;
  unmute: () => void;
  isMuted: () => boolean;
  sendDigits: (digits: string) => void;
  reject: () => void;
  /** Transfer the active call to another number via the backend (blind by default). */
  transfer: (to: string, warm?: boolean) => Promise<void>;
  /** Add another participant to this call's conference (after a warm transfer/merge). */
  conference: (to: string) => Promise<void>;
}

async function fetchToken(): Promise<{ token: string; identity: string }> {
  return apiClient.get<{ token: string; identity: string }>("/voice/token");
}

interface IceServer {
  urls: string | string[];
  username?: string;
  credential?: string;
}

// Ephemeral STUN + TURN credentials so calls relay through TURN on restrictive
// networks (corporate/public Wi-Fi that blocks direct/STUN). Non-fatal: if this
// fails the SDK falls back to Twilio's default edge behaviour.
async function fetchIceServers(): Promise<IceServer[] | undefined> {
  try {
    const res = await apiClient.get<{ iceServers: IceServer[] }>("/voice/ice-servers");
    return res.iceServers?.length ? res.iceServers : undefined;
  } catch {
    return undefined;
  }
}

export function useTwilioVoice(): UseTwilioVoice {
  const [device, setDevice] = useState<Device | null>(null);
  const [activeCall, setActiveCall] = useState<Call | null>(null);
  const [activeCallId, setActiveCallId] = useState<string | null>(null);
  const [incoming, setIncoming] = useState<IncomingInfo | null>(null);
  const [status, setStatus] = useState<VoiceStatus>("idle");
  const [identity, setIdentity] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const deviceRef = useRef<Device | null>(null);
  const callRef = useRef<Call | null>(null);
  const activeCallIdRef = useRef<string | null>(null);
  const iceServersRef = useRef<IceServer[] | undefined>(undefined);

  const clearActiveCallId = useCallback(() => {
    activeCallIdRef.current = null;
    setActiveCallId(null);
  }, []);

  // Registers lifecycle listeners and tracks the call — deliberately does NOT
  // set the status. Callers own the state transition: incoming calls must stay
  // in "incoming" (Answer/Decline screen) until the user accepts, and outbound
  // calls stay "connecting" until media is up ("accept" fires).
  const attachCall = useCallback((call: Call) => {
    callRef.current = call;
    setActiveCall(call);
    call.on("accept", () => {
      setIncoming(null);
      setStatus("in-call");
    });
    call.on("connected", () => {
      setStatus("in-call");
    });
    call.on("disconnect", () => {
      callRef.current = null;
      setActiveCall(null);
      setIncoming(null);
      clearActiveCallId();
      setStatus("ready");
    });
    call.on("cancel", () => {
      callRef.current = null;
      setActiveCall(null);
      setIncoming(null);
      clearActiveCallId();
      setStatus("ready");
    });
    call.on("reject", () => {
      callRef.current = null;
      setActiveCall(null);
      setIncoming(null);
      clearActiveCallId();
      setStatus("ready");
    });
    call.on("error", (err?: unknown) => {
      setError(err instanceof Error ? err.message : "Call error");
      callRef.current = null;
      setActiveCall(null);
      setIncoming(null);
      clearActiveCallId();
      setStatus("ready");
    });
  }, [clearActiveCallId]);

  const init = useCallback(async () => {
    setStatus("initializing");
    try {
      const { token, identity: id } = await fetchToken();
      setIdentity(id);
      // Fetch once and reuse per call. In the Voice SDK, rtcConfiguration is
      // applied on connect()/accept(), not on the Device constructor.
      iceServersRef.current = await fetchIceServers();
      const d = new Device(token, {
        appName: "aria",
        closeProtection: true,
      });
      deviceRef.current = d;
      setDevice(d);

      d.on("ready", () => setStatus("ready"));
      d.on("error", (err?: unknown) => {
        setError(err instanceof Error ? err.message : "Device error");
        setStatus("error");
      });
      d.on("offline", () => setStatus("offline"));
      d.on("registered", () => setStatus("ready"));
      d.on("incoming", (call: Call) => {
        const from = String(call.parameters?.From ?? "");
        attachCall(call);
        setIncoming({ call, from });
        setStatus("incoming");
      });

      try {
        await d.register();
      } catch {
        // register may fail on some browsers; ignore
      }
      setStatus((s) => (s === "incoming" ? s : "ready"));
    } catch (e) {
      setError(e instanceof Error ? e.message : "Failed to init voice");
      setStatus("error");
    }
  }, [attachCall]);

  useEffect(() => {
    init();
    return () => {
      try {
        deviceRef.current?.destroy();
      } catch {
        // ignore
      }
      deviceRef.current = null;
    };
  }, [init]);

  const dial = useCallback(
    async (to: string, opts?: { record?: boolean; conferenceRoomId?: string }) => {
      const d = deviceRef.current;
      if (!d) throw new Error("Voice device not ready");
      clearActiveCallId();
      try {
        const res = await apiClient.post<{ callId?: string }>("/voice/outbound", {
          to,
          record: opts?.record,
          conferenceRoomId: opts?.conferenceRoomId,
        });
        // Remember the backend call id so transfer/hangup APIs can target it.
        const newCallId = res?.callId;
        if (newCallId) {
          activeCallIdRef.current = newCallId;
          setActiveCallId(newCallId);
        }
      } catch {
        // continue: connect may still dial via params
      }
      setStatus("connecting");
      const call = await d.connect({
        params: { To: to },
        ...(iceServersRef.current ? { rtcConfiguration: { iceServers: iceServersRef.current } } : {}),
      });
      attachCall(call);
    },
    [attachCall, clearActiveCallId]
  );

  const answer = useCallback(() => {
    const call = incoming?.call ?? callRef.current;
    if (!call) return;
    try {
      call.accept(
        iceServersRef.current ? { rtcConfiguration: { iceServers: iceServersRef.current } } : undefined,
      );
    } catch {
      // ignore
    }
    // Listeners are already attached from the "incoming" handler; the call's
    // "accept" event clears `incoming` and moves status to "in-call".
    setIncoming(null);
    setStatus("in-call");
  }, [incoming]);

  const reject = useCallback(() => {
    const call = incoming?.call ?? callRef.current;
    if (!call) return;
    try {
      call.reject();
    } catch {
      // ignore
    }
    setIncoming(null);
    callRef.current = null;
    setActiveCall(null);
    clearActiveCallId();
    setStatus("ready");
  }, [incoming, clearActiveCallId]);

  const hangup = useCallback(() => {
    const call = callRef.current ?? activeCall;
    if (!call) {
      deviceRef.current?.disconnectAll();
      return;
    }
    try {
      call.disconnect();
    } catch {
      // ignore
    }
    callRef.current = null;
    setActiveCall(null);
    setIncoming(null);
    clearActiveCallId();
    setStatus("ready");
  }, [activeCall, clearActiveCallId]);

  const mute = useCallback(() => {
    callRef.current?.mute(true);
  }, []);
  const unmute = useCallback(() => {
    callRef.current?.mute(false);
  }, []);
  const isMuted = useCallback(() => Boolean(callRef.current?.isMuted()), []);
  const sendDigits = useCallback((digits: string) => {
    callRef.current?.sendDigits(digits);
  }, []);

  const transfer = useCallback(async (to: string, warm = false): Promise<void> => {
    const id = activeCallIdRef.current;
    if (!id) throw new Error("Transfer isn't available for this call yet.");
    await apiClient.post(`/voice/${id}/transfer`, { to, warm });
  }, []);

  const conference = useCallback(async (to: string): Promise<void> => {
    const id = activeCallIdRef.current;
    if (!id) throw new Error("Conference isn't available for this call yet.");
    await apiClient.post(`/voice/${id}/conference`, { participantPhone: to });
  }, []);

  return {
    device,
    activeCall,
    activeCallId,
    incoming,
    status,
    identity,
    error,
    dial,
    answer,
    hangup,
    conference,
    mute,
    unmute,
    isMuted,
    sendDigits,
    reject,
    transfer,
  };
}